import { styled } from "@mui/material/styles";

// ── Page root ─────────────────────────────────────────────────────────────────

export const ProgramDetailRoot = styled("div")({
  margin: "0 auto",
  fontFamily: "Poppins, sans-serif",
});

// ── Back button ───────────────────────────────────────────────────────────────

export const ProgramDetailBackBtn = styled("button")({
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  background: "none",
  border: "none",
  color: "#A87EFF",
  fontSize: 14,
  fontFamily: "Poppins, sans-serif",
  fontWeight: 500,
  cursor: "pointer",

  marginBottom: 20,
  transition: "color 150ms",
  "&:hover": {
    color: "#FEFEFE",
  },
});

// ── Hero ──────────────────────────────────────────────────────────────────────

export const ProgramDetailHero = styled("div")({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 32,
  alignItems: "start",
  "@media (max-width: 900px)": {
    gridTemplateColumns: "1fr",
  },
});

// ── Poster (rounded frame) ────────────────────────────────────────────────────

export const ProgramDetailPosterWrap = styled("div")({
  position: "relative",
  borderRadius: 20,
  overflow: "hidden",
  aspectRatio: "4 / 3",
});

export const ProgramDetailPosterImg = styled("div")(({ $src }) => ({
  position: "absolute",
  inset: 0,
  backgroundImage: `url(${$src})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
}));

export const ProgramDetailPosterTint = styled("div")(({ $a, $b }) => ({
  position: "absolute",
  inset: 0,
  background: `linear-gradient(135deg, ${$a}33 0%, ${$b}44 100%), linear-gradient(180deg, transparent 35%, rgba(3,0,57,0.92) 100%)`,
}));

export const ProgramDetailPosterContent = styled("div")({
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  padding: "24px 28px",
  display: "flex",
  flexDirection: "column",
  gap: 8,
});

export const ProgramDetailPillRow = styled("div")({
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
});

export const ProgramDetailHeroTitle = styled("h1")({
  fontSize: 28,
  fontWeight: 700,
  color: "#FEFEFE",
  margin: 0,
  lineHeight: 1.2,
  letterSpacing: "-0.02em",
  "@media (max-width: 1100px)": {
    fontSize: 22,
  },
});

export const ProgramDetailGuruRow = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 8,
});

export const ProgramDetailGuruAvatar = styled("div")(({ $src }) => ({
  width: 30,
  height: 30,
  borderRadius: "50%",
  backgroundImage: `url(${$src})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  background: "#211F45",
  flexShrink: 0,
  boxShadow: "0 0 0 2px rgba(255,255,255,0.3)",
}));

export const ProgramDetailGuruText = styled("span")({
  fontSize: 13,
  color: "rgba(255,255,255,0.75)",
  fontFamily: "Poppins, sans-serif",
  lineHeight: 1.4,
});

export const ProgramDetailGuruName = styled("span")({
  fontSize: 13,
  fontWeight: 600,
  color: "#01ECFF",
  fontFamily: "Poppins, sans-serif",
});

// kept for compat – no longer rendered but avoids import errors if referenced
export const ProgramDetailGuruRole = styled("span")({
  fontSize: 12,
  color: "#A87EFF",
  fontFamily: "Poppins, sans-serif",
});

// ── Sidebar ───────────────────────────────────────────────────────────────────

export const ProgramDetailSidebar = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 18,
  paddingTop: 4,
});

// ── Overview label + blurb ────────────────────────────────────────────────────

export const ProgramDetailOverviewLabel = styled("p")({
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.10em",
  textTransform: "uppercase",
  color: "#A87EFF",
  margin: "0 0 15px 0",
  fontFamily: "Poppins, sans-serif",
});

export const ProgramDetailBlurb = styled("p")({
  fontSize: 15,
  color: "#D1C7FF",
  margin: 0,
  lineHeight: 1.65,
  marginTop: -10,
});

// ── Stats grid (4 individual bordered boxes) ──────────────────────────────────

export const ProgramDetailStatsGrid = styled("div")({
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: 10,
  "@media (max-width: 600px)": {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
});

export const ProgramDetailStatItem = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 6,
  padding: "12px 14px",
  borderRadius: 12,
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.09)",
});

export const ProgramDetailStatIconRow = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 5,
});

export const ProgramDetailStatLabel = styled("span")({
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "#A87EFF",
  fontFamily: "Poppins, sans-serif",
});

export const ProgramDetailStatNum = styled("span")({
  fontSize: 16,
  fontWeight: 700,
  color: "#FEFEFE",
  fontFamily: "Poppins, sans-serif",
  lineHeight: 1.2,
});

// ── Status banner ─────────────────────────────────────────────────────────────

export const ProgramDetailStatusBanner = styled("div")(({ $type }) => ({
  display: "flex",
  alignItems: "center",
  gap: 14,
  padding: "14px 18px",
  borderRadius: 14,
  background:
    $type === "trial"
      ? "linear-gradient(95deg, rgba(1,236,255,0.08), rgba(150,46,255,0.12))"
      : $type === "completed"
        ? "rgba(23,178,106,0.08)"
        : "rgba(1,236,255,0.06)",
  border:
    $type === "trial"
      ? "1px solid rgba(1,236,255,0.28)"
      : $type === "completed"
        ? "1px solid rgba(23,178,106,0.28)"
        : "1px solid rgba(1,236,255,0.18)",
}));

export const ProgramDetailStatusMain = styled("p")(({ $type }) => ({
  fontSize: 14,
  fontWeight: 700,
  color:
    $type === "completed" ? "#17B26A" : "#FEFEFE",
  margin: 0,
  fontFamily: "Poppins, sans-serif",
}));

export const ProgramDetailStatusSub = styled("p")({
  fontSize: 12,
  color: "#A87EFF",
  margin: "2px 0 0",
  fontFamily: "Poppins, sans-serif",
});

// kept for compat
export const ProgramDetailStatusLabel = styled("span")({
  fontSize: 13,
  fontWeight: 600,
  color: "#01ECFF",
  fontFamily: "Poppins, sans-serif",
});

// ── Progress tile ─────────────────────────────────────────────────────────────

export const ProgramDetailProgressSection = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 10,
  padding: "16px 18px",
  borderRadius: 14,
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.09)",
});

export const ProgramDetailProgressLabel = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});

export const ProgramDetailProgressHeading = styled("span")({
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.10em",
  textTransform: "uppercase",
  color: "#A87EFF",
  fontFamily: "Poppins, sans-serif",
});

export const ProgramDetailProgressPct = styled("span")(({ $isCompleted }) => ({
  fontSize: 14,
  fontWeight: 700,
  color: $isCompleted ? "#17B26A" : "#FEFEFE",
  fontFamily: "Poppins, sans-serif",
}));

export const ProgramDetailProgressTrack = styled("div")({
  height: 8,
  borderRadius: 999,
  background: "rgba(255,255,255,0.10)",
  overflow: "hidden",
});

export const ProgramDetailProgressFill = styled("div")(({ $pct, $isCompleted }) => ({
  height: "100%",
  width: `${$pct}%`,
  background: $isCompleted
    ? "linear-gradient(90deg, #17B26A, #7FE5B0)"
    : "linear-gradient(90deg, #01ECFF, #962EFF)",
  borderRadius: 999,
  transition: "width 400ms ease",
}));

export const ProgramDetailProgressSessionsRow = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  gap: 10,
  flexWrap: "wrap",
});

export const ProgramDetailProgressSessionChip = styled("span")({
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  fontSize: 12,
  color: "#D1C7FF",
  fontFamily: "Poppins, sans-serif",
});

// ── Action buttons ────────────────────────────────────────────────────────────

export const ProgramDetailActions = styled("div")({
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
});

export const ProgramDetailBtnSecondary = styled("button")({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  height: 44,
  padding: "0 20px",
  borderRadius: 10,
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.14)",
  color: "#D1C7FF",
  fontSize: 14,
  fontWeight: 500,
  fontFamily: "Poppins, sans-serif",
  cursor: "pointer",
  flex: 1,
  transition: "background 150ms, border-color 150ms",
  "&:hover": {
    background: "rgba(255,255,255,0.10)",
    borderColor: "rgba(255,255,255,0.24)",
  },
});

// ── Topics section ────────────────────────────────────────────────────────────

export const ProgramDetailTopicsSection = styled("section")({
  marginTop: "32px"
});

export const ProgramDetailTopicsHeader = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 20,
});

export const ProgramDetailTopicsTitle = styled("h2")({
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.10em",
  textTransform: "uppercase",
  color: "#A87EFF",
  margin: 0,
  fontFamily: "Poppins, sans-serif",
});

export const ProgramDetailTopicsList = styled("div")({
  display: "flex",
  flexDirection: "column",
  borderRadius: 16,
  background: "rgba(255,255,255,0.02)",
  border: "1px solid rgba(255,255,255,0.07)",
  overflow: "hidden",
  padding: "8px 24px 0px"
});

// ── Upgrade strip ─────────────────────────────────────────────────────────────

export const ProgramDetailUpgradeStrip = styled("div")({
  marginTop: "32px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 16,
  padding: "24px 32px",
  borderRadius: 16,
  background: "linear-gradient(95deg, rgba(1,236,255,0.08) 0%, rgba(150,46,255,0.10) 100%)",
  border: "1px solid rgba(1,236,255,0.2)",
  flexWrap: "wrap",
});

export const ProgramDetailUpgradeText = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 4,
});

export const ProgramDetailUpgradeTitle = styled("h3")({
  fontSize: 20,
  fontWeight: 700,
  color: "#FEFEFE",
  margin: 0,
  fontFamily: "Poppins, sans-serif",
});

export const ProgramDetailUpgradeSubtitle = styled("p")({
  fontSize: 14,
  color: "#A87EFF",
  margin: 0,
  fontFamily: "Poppins, sans-serif",
});

// ── Pill ──────────────────────────────────────────────────────────────────────

export const DetailPill = styled("span")(({ $bg, $fg, $bd }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "3px 10px",
  borderRadius: 999,
  background: $bg,
  color: $fg,
  border: `1px solid ${$bd}`,
  fontSize: 11,
  fontWeight: 600,
  fontFamily: "Poppins, sans-serif",
  whiteSpace: "nowrap",
}));
